import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { isNewerSnapshot } from './snapshotOrder';
import { useRushMidMatch, type RushMidSnapshot } from './useRushMidMatch';

const POLL_MS = 1000;

export interface SnapshotSync {
  snapshot: RushMidSnapshot | null;
  status: RushMidSnapshot['status'];
  /** monotonic apply — ignores a snapshot that would go backwards */
  apply: (next: RushMidSnapshot) => void;
  /** authoritative set for deliberate downgrades (cancel / OVER→PARTY on "back to lobby"); also
   *  invalidates any poll in flight so a stale one can't undo it */
  setSnapshot: Dispatch<SetStateAction<RushMidSnapshot | null>>;
  /** current command generation — capture before an async recovery fetch and compare before
   *  applying its result, so a newer command's snapshot is never overwritten by it */
  generation: () => number;
}

/**
 * Owns the authoritative snapshot state over HTTP (no Centrifugo): monotonic apply,
 * resume-on-open (drop a returning player back into their live phase — but never re-open a
 * finished OVER match), and the ~1s poll while waiting (PARTY / OPEN / BUYING / PLAYING).
 */
export function useSnapshotSync(mm: ReturnType<typeof useRushMidMatch>): SnapshotSync {
  const [snapshot, setSnapshot] = useState<RushMidSnapshot | null>(null);
  const status = snapshot?.status ?? 'NONE';

  // Bumped by every authoritative local change (command apply / deliberate downgrade). A poll
  // captures it before fetching and drops its result if it changed meanwhile — that poll may have
  // read pre-command state, and applying it would bounce the UI backwards.
  const cmdGen = useRef(0);

  const apply = useCallback((next: RushMidSnapshot) => {
    cmdGen.current++;
    setSnapshot((prev) => (isNewerSnapshot(next, prev) ? next : prev));
  }, []);

  // Background-source fold (poll + visibility fetch): monotonic, PLUS it holds the result screen.
  // The live match row is deleted ~120s after it ends, so a poll then reads NONE — but the player
  // may still be reading their result. Keep the OVER snapshot; the user leaves it deliberately via
  // "again" (which sets NONE itself). A genuine next state (reformed PARTY, a new match) is not
  // NONE and still applies. Only a command-driven apply/setAuthoritative can move off OVER.
  const foldBackground = useCallback((prev: RushMidSnapshot | null, s: RushMidSnapshot) => {
    if (prev?.status === 'OVER' && s.status === 'NONE') return prev;
    // Never ENTER a finished match from a non-match state on a background read. The server keeps
    // a finished match ~120s (the result-screen retention), so a visibility refetch right after
    // reopening the app would resurrect the previous game — and replay its battle. Reaching OVER
    // is legit only as a transition of the match already on screen (same or forward matchId).
    if (s.status === 'OVER' && !(prev && 'matchId' in prev)) return prev;
    return isNewerSnapshot(s, prev) ? s : prev;
  }, []);

  const setAuthoritative = useCallback<Dispatch<SetStateAction<RushMidSnapshot | null>>>((v) => {
    cmdGen.current++;
    setSnapshot(v);
  }, []);

  // resume on app open — a player who closed the app mid-match is dropped straight back into
  // searching/buy/battle. OVER is terminal (the row lingers ~120s) — never re-open it.
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current) return;
    resumedRef.current = true;
    mm.getSnapshot().then((s) => {
      if (s.status === 'OVER') return;
      // Resume only SEEDS an empty state. It races the deep-link join effect: a fresh invitee's
      // resume reads NONE (not joined yet) while the join sets PARTY — if that stale NONE landed
      // last it would drop the invitee onto the solo lobby. So never let a resume overwrite a
      // snapshot a command already set; otherwise apply monotonically.
      setSnapshot((prev) => {
        if (prev && s.status === 'NONE') return prev;
        return isNewerSnapshot(s, prev) ? s : prev;
      });
    }).catch(() => {});
  }, [mm]);

  // Fast resync on app return: the TMA webview suspends JS entirely while the user is in the
  // Telegram chat (e.g. sending the party invite), so the interval poll freezes and the local
  // state goes stale — and a NONE state isn't polled at all. One immediate fetch on becoming
  // visible catches whatever happened while suspended (pruned from party, seated into a match).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const gen = cmdGen.current;
      mm.getSnapshot().then((s) => {
        if (cmdGen.current !== gen) return;
        setSnapshot((prev) => foldBackground(prev, s));
      }).catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [mm, foldBackground]);

  // poll the snapshot while waiting; stop otherwise.
  useEffect(() => {
    // OVER polls too: a party member idling on the result screen must catch their leader
    // restarting (the server re-seats them into a new match — a different matchId, accepted by
    // isNewerSnapshot). A solo re-reads the same finished match (no-op) until it expires to NONE.
    if (status !== 'PARTY' && status !== 'OPEN' && status !== 'BUYING' && status !== 'PLAYING' && status !== 'OVER') return;
    const id = window.setInterval(() => {
      const gen = cmdGen.current;
      mm.getSnapshot().then((s) => {
        if (cmdGen.current !== gen) return; // a command landed while this poll was in flight → drop it
        setSnapshot((prev) => foldBackground(prev, s));
      }).catch(() => {});
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [status, mm, foldBackground]);

  return { snapshot, status, apply, setSnapshot: setAuthoritative, generation: useCallback(() => cmdGen.current, []) };
}
